import React, { useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useCreatePersonMutation } from '../../redux/api/personsApi';
import AddPersonPage from '../../pages/persons/AddPersonPage';
import { toast } from 'react-toastify';

const AddCustomerModal = ({ show, onHide, onCustomerCreated }) => {
  const [createPerson, { isLoading: loading }] = useCreatePersonMutation();
  const [formData, setFormData] = useState(null);

  // Memoize callback to prevent infinite re-renders in child component
  const handleFormChange = useCallback((data) => {
    setFormData(data);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (!formData.person_type_id) {
        toast.error('Please select a person type');
        return;
      }

      // Extract addresses and noAddress from formData - these are UI-only fields
      const { addresses, noAddress, ...personFields } = formData;
      
      // Filter to only include addresses with valid address_type_id
      const validAddresses = !noAddress && addresses 
        ? addresses.filter(addr => addr.address_type_id)
        : [];
      
      // Build clean submission data
      const submissionData = {
        first_name: personFields.first_name,
        last_name: personFields.last_name,
        email: personFields.email,
        phone: personFields.phone || '',
        person_type_id: personFields.person_type_id,
        is_active: personFields.is_active ?? true,
        person_type: "customer",
      };

      // Only include addresses if we have valid ones
      if (validAddresses.length > 0) {
        submissionData.addresses = validAddresses.map(addr => ({
          address_type_id: addr.address_type_id,
          street: addr.street || '',
          city: addr.city || '',
          state: addr.state || '',
          postal_code: addr.postal_code || '',
          country: addr.country || '',
          floor: addr.floor || null,
          address_type: 'billing'
        }));
      }

      const person = await createPerson(submissionData).unwrap();
      toast.success('Customer created successfully!');
      onCustomerCreated(person);
      onHide();
      setFormData(null);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Failed to create customer');
    }
  };

  const isFormValid = formData && 
    formData.first_name && 
    formData.last_name && 
    formData.email && 
    formData.person_type_id;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add New Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AddPersonPage
          onSubmit={handleFormChange}
          isModal={true}
          hideSubmitButton={true}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !isFormValid}
        >
          {loading ? "Creating..." : "Create Customer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddCustomerModal;
